"use client";

import { type FC, type PropsWithChildren, useCallback, useMemo, useState } from "react";
import useAgreement from "../hooks/useAgreement";
import useDimensionsForm from "../hooks/useDimensionsForm";
import useShapes from "../hooks/useShapes";
import { AddToCartContext } from "./AddToCartContext";
import { useCardContext } from "./CardContext";
import { useConfig } from "./ConfigContext";
import { usePostDataContext } from "./PostDataContext";

export const AddToCartContextProvider: FC<PropsWithChildren> = ({ children }) => {
	const { data, workpiece } = usePostDataContext();
	const { invalidCards } = useCardContext();
	const { onAddToCart, onAddToProject } = useConfig();
	const { shape } = useShapes();
	const { validate } = useDimensionsForm(shape);
	const { validate: validateAgreement } = useAgreement();
	const [isLoading, setLoading] = useState(false);

	const canAddToCart = useCallback(async () => {
		if (shape) {
			const validDimensions = await validate({ fields: shape.inputs.map((input) => input.id) });
			const validAgreement = await validateAgreement(workpiece.accepted_condition);

			return validDimensions && validAgreement;
		}

		return false;
	}, [shape, validate, validateAgreement, workpiece.accepted_condition]);

	const onAddToCartClick = async () => {
		setLoading(true);

		if (await canAddToCart()) {
			await onAddToCart(data, workpiece);
		}

		setLoading(false);
	};

	const onAddToProjectClick = async (name: string, id?: string | "newProject") => {
		setLoading(true);

		if (await canAddToCart()) {
			await onAddToProject(data, workpiece, name, id);
		}

		setLoading(false);
	};

	const isDisabled = useMemo(() => !!invalidCards.length, [invalidCards.length]);

	return (
		<AddToCartContext.Provider
			value={{
				onAddToProjectClick,
				onAddToCartClick,
				isLoading,
				isDisabled,
			}}
		>
			{children}
		</AddToCartContext.Provider>
	);
};
